'use client';

import { SceneNotifications } from '../../collection-showcase/scenes/scene-primitives.jsx';
import { ShowcaseIcon } from '../../collection-showcase/scenes/scene-primitives.jsx';
import { CompletionMark } from '../../collection-showcase/scenes/playback-controls.jsx';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import {
  ShowcaseCopyContext,
  translateShowcaseNode,
} from '../../collection-showcase/translation';
import { useBusinessPlayback } from './business-primitives';
import { wa, Ca, xa, Ta, ba, ApprovalCard, Sa } from './approval-card';
export function ApprovalsAnimation({ running: e, onInteract: t, onDone: n }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let { t: r } = useBusinessPlayback(wa, {
      running: e,
      onDone: n,
    }),
    [i, a] = React.useState(null),
    o = r >= Ca.approved ? 'approved' : r >= Ca.sending ? 'sending' : 'pending',
    s = i?.decision ?? o,
    c = i?.enabled ?? new Set(xa.map((e) => e.key)),
    l =
      s === 'approved' || s === 'denied'
        ? Ta[s].filter(
            (e) => s === 'denied' || c.has(e.id === 'sent' ? 'email' : 'store')
          )
        : [],
    u = (e) => {
      (t(),
        a({
          decision: s,
          enabled: c,
          ...e,
        }));
    };
  return (
    <div
      className={
        'grid gap-4 p-2 md:p-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:gap-6'
      }
    >
      <div
        className={
          'flex flex-col gap-2.5 rounded-[10px] bg-[var(--background)] p-3 shadow-[var(--sh-1)] sm:p-5 lg:min-h-[600px]'
        }
      >
        <div className={'flex justify-end'}>
          <p
            className={
              'text-ink max-w-[92%] rounded-[18px] bg-[var(--secondary)] px-4 py-2 text-[15px] leading-6 shadow-[var(--shadow-minimal)] sm:max-w-[84%]'
            }
          >
            {translateShowcaseNode(showcaseCopy, ba)}
          </p>
        </div>
        <div
          className={'text-ink-3 flex h-7 items-center gap-2 px-1 text-[14px]'}
        >
          <CompletionMark done={r >= Ca.worked} className={'size-4 shrink-0'} />
          <span className={'num'}>
            {translateShowcaseNode(
              showcaseCopy,
              r >= Ca.worked ? 'Worked for 41s' : 'Drafting'
            )}
          </span>
          <ShowcaseIcon name={'chevronDown'} className={'size-4 shrink-0'} />
        </div>
        {translateShowcaseNode(
          showcaseCopy,
          r >= Ca.worked ? (
            <p className={'enter text-ink px-1 text-[15px] leading-6'}>
              {translateShowcaseNode(
                showcaseCopy,
                'I drafted the counter-offer and the three price changes. Both need your approval before anything goes out.'
              )}
            </p>
          ) : null
        )}
        {translateShowcaseNode(
          showcaseCopy,
          r >= Ca.card ? (
            <ApprovalCard
              actions={xa}
              enabled={c}
              decision={s}
              pressed={!i && r >= Ca.press && r < Ca.sending}
              onToggle={(e) => {
                let t = new Set(c);
                (t.has(e) ? t.delete(e) : t.add(e),
                  u({
                    enabled: t,
                  }));
              }}
              onApprove={() =>
                u({
                  decision: 'approved',
                })
              }
              onDeny={() =>
                u({
                  decision: 'denied',
                })
              }
            />
          ) : null
        )}
        <div className={'pt-4'}>
          <SceneNotifications
            toasts={l}
            label={'Action results'}
            className={'w-[min(340px,100%)]'}
          />
        </div>
      </div>
      <div className={'flex flex-col'}>
        <p className={'label px-2 pb-2'}>
          {translateShowcaseNode(showcaseCopy, 'Works in your tools')}
        </p>
        <ul
          className={'sf grid grid-cols-2 rounded-[14px] p-1.5 sm:grid-cols-1'}
        >
          {translateShowcaseNode(
            showcaseCopy,
            Sa.map((e) => (
              <li
                key={e.label}
                className={
                  'text-ink flex h-12 min-w-0 items-center gap-2.5 rounded-[10px] px-2 text-[13px] sm:gap-3 sm:px-3 sm:text-[14px]'
                }
              >
                <span
                  className={
                    'grid size-8 shrink-0 place-items-center rounded-md bg-[var(--background)] shadow-[inset_0_0_0_1px_var(--border)]'
                  }
                >
                  <ShowcaseIcon name={e.icon} className={'text-ink-2 size-4'} />
                </span>
                <span className={'min-w-0 flex-1 truncate'}>
                  {translateShowcaseNode(showcaseCopy, e.label)}
                </span>
                <span
                  className={
                    'text-ink-3 flex shrink-0 items-center gap-1.5 text-[12px]'
                  }
                >
                  <ShowcaseIcon name={'lock'} className={'size-3.5'} />
                  <span className={'max-sm:sr-only'}>
                    {translateShowcaseNode(showcaseCopy, 'Asks first')}
                  </span>
                </span>
              </li>
            ))
          )}
          <li
            className={
              'text-ink col-span-2 mt-1 flex h-12 items-center gap-3 rounded-[10px] bg-[var(--tint)] px-3 text-[14px] sm:col-span-1'
            }
          >
            <span
              className={
                'grid size-8 shrink-0 place-items-center rounded-md bg-[var(--background)] shadow-[inset_0_0_0_1px_var(--border)]'
              }
            >
              <ShowcaseIcon name={'clock'} className={'text-ink-2 size-4'} />
            </span>
            <span className={'min-w-0 flex-1 truncate'}>
              {translateShowcaseNode(showcaseCopy, 'Recurring work')}
            </span>
            <span className={'text-ink-3 text-[12px]'}>
              {translateShowcaseNode(showcaseCopy, 'On a schedule')}
            </span>
          </li>
        </ul>
        <p className={'text-ink-3 mt-3 px-2 text-[12.5px] leading-[1.5]'}>
          {translateShowcaseNode(
            showcaseCopy,
            'Approved content can be sent to connected platforms where direct publishing is supported. Your team controls approval, scheduling and release.'
          )}
        </p>
      </div>
    </div>
  );
}
export function ImaiAvatar({ svg: e, size: t, className: n = '' }) {
  return (
    <span
      aria-hidden={!0}
      className={`bz-blob ${n}`}
      style={{
        width: t,
        height: t,
      }}
      dangerouslySetInnerHTML={{
        __html: e,
      }}
    />
  );
}
export const Oa =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="-158 -158 316 316"><defs></defs><path d="M84.293 0C85.896 2.776 87.553 5.646 89.375 8.803C91.198 11.96 93.381 15.33 95.229 18.942C97.078 22.554 99.433 26.631 100.464 30.475C101.495 34.32 101.718 38.299 101.417 42.008C101.115 45.718 100.112 49.413 98.654 52.732C97.195 56.05 95.13 59.234 92.666 61.917C90.201 64.6 87.209 67.045 83.868 68.828C80.527 70.612 76.613 71.925 72.62 72.62C68.627 73.315 63.883 72.937 59.91 73C55.936 73.063 52.259 73 48.777 73C45.295 73 42.109 73 39.019 73C35.929 73 33.05 73 30.238 73C27.425 73 24.764 73 22.144 73C19.525 73 17.013 73 14.521 73C12.028 73 9.61 73 7.19 73C4.77 73 2.397 73 0 73C-2.397 73 -4.77 73 -7.19 73C-9.61 73 -12.028 73 -14.521 73C-17.013 73 -19.525 73 -22.144 73C-24.764 73 -27.425 73 -30.238 73C-33.05 73 -35.929 73 -39.019 73C-42.109 73 -45.295 73 -48.777 73C-52.259 73 -55.936 73.063 -59.91 73C-63.883 72.937 -68.627 73.315 -72.62 72.62C-76.613 71.925 -80.527 70.612 -83.868 68.828C-87.209 67.045 -90.201 64.6 -92.666 61.917C-95.13 59.234 -97.195 56.05 -98.654 52.732C-100.112 49.413 -101.115 45.718 -101.417 42.008C-101.718 38.299 -101.495 34.32 -100.464 30.475C-99.433 26.631 -97.078 22.554 -95.229 18.942C-93.381 15.33 -91.198 11.96 -89.375 8.803C-87.553 5.646 -85.896 2.776 -84.293 0C-82.69 -2.776 -81.205 -5.349 -79.758 -7.855C-78.311 -10.362 -76.948 -12.722 -75.61 -15.04C-74.272 -17.357 -72.995 -19.57 -71.73 -21.759C-70.466 -23.949 -69.244 -26.065 -68.025 -28.177C-66.806 -30.289 -65.614 -32.353 -64.415 -34.43C-63.215 -36.508 -62.031 -38.559 -60.828 -40.644C-59.624 -42.728 -58.425 -44.805 -57.194 -46.938C-55.962 -49.07 -54.725 -51.214 -53.44 -53.44C-52.154 -55.666 -50.852 -57.921 -49.482 -60.294C-48.112 -62.667 -46.713 -65.09 -45.22 -67.677C-43.727 -70.263 -42.191 -72.924 -40.523 -75.813C-38.855 -78.702 -37.128 -81.696 -35.212 -85.01C-33.297 -88.325 -31.423 -92.392 -29.03 -95.699C-26.637 -99.006 -23.885 -102.432 -20.856 -104.852C-17.827 -107.272 -14.332 -109.029 -10.856 -110.22C-7.38 -111.412 -3.619 -112 0 -112C3.619 -112 7.38 -111.412 10.856 -110.22C14.332 -109.029 17.827 -107.272 20.856 -104.852C23.885 -102.432 26.637 -99.006 29.03 -95.699C31.423 -92.392 33.297 -88.325 35.212 -85.01C37.128 -81.696 38.855 -78.702 40.523 -75.813C42.191 -72.924 43.727 -70.263 45.22 -67.677C46.713 -65.09 48.112 -62.667 49.482 -60.294C50.852 -57.921 52.154 -55.666 53.44 -53.44C54.725 -51.214 55.962 -49.07 57.194 -46.938C58.425 -44.805 59.624 -42.728 60.828 -40.644C62.031 -38.559 63.215 -36.508 64.415 -34.43C65.614 -32.353 66.806 -30.289 68.025 -28.177C69.244 -26.065 70.466 -23.949 71.73 -21.759C72.995 -19.57 74.272 -17.357 75.61 -15.04C76.948 -12.722 78.311 -10.362 79.758 -7.855C81.205 -5.349 82.69 -2.776 84.293 0Z" fill="#8f6bff"/><path data-sq-eye="0" d="M-13.5 0A8.5 8.5 0 0 1 -5 -8.5L5 -8.5A8.5 8.5 0 0 1 13.5 0L13.5 0A8.5 8.5 0 0 1 5 8.5L-5 8.5A8.5 8.5 0 0 1 -13.5 0Z" transform="matrix(0.95 0.19 -0.22 0.97 -12.822 -9.501)" fill="#fff"/><path data-sq-eye="1" d="M-13.5 0A8.5 8.5 0 0 1 -5 -8.5L5 -8.5A8.5 8.5 0 0 1 13.5 0L13.5 0A8.5 8.5 0 0 1 5 8.5L-5 8.5A8.5 8.5 0 0 1 -13.5 0Z" transform="matrix(0.89 -0.19 0.24 0.97 23.759 -9.31)" fill="#fff"/></svg>';
export const ka =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="-158 -158 316 316"><defs></defs><path d="M84.293 0C85.896 2.776 87.553 5.646 89.375 8.803C91.198 11.96 93.381 15.33 95.229 18.942C97.078 22.554 99.433 26.631 100.464 30.475C101.495 34.32 101.718 38.299 101.417 42.008C101.115 45.718 100.112 49.413 98.654 52.732C97.195 56.05 95.13 59.234 92.666 61.917C90.201 64.6 87.209 67.045 83.868 68.828C80.527 70.612 76.613 71.925 72.62 72.62C68.627 73.315 63.883 72.937 59.91 73C55.936 73.063 52.259 73 48.777 73C45.295 73 42.109 73 39.019 73C35.929 73 33.05 73 30.238 73C27.425 73 24.764 73 22.144 73C19.525 73 17.013 73 14.521 73C12.028 73 9.61 73 7.19 73C4.77 73 2.397 73 0 73C-2.397 73 -4.77 73 -7.19 73C-9.61 73 -12.028 73 -14.521 73C-17.013 73 -19.525 73 -22.144 73C-24.764 73 -27.425 73 -30.238 73C-33.05 73 -35.929 73 -39.019 73C-42.109 73 -45.295 73 -48.777 73C-52.259 73 -55.936 73.063 -59.91 73C-63.883 72.937 -68.627 73.315 -72.62 72.62C-76.613 71.925 -80.527 70.612 -83.868 68.828C-87.209 67.045 -90.201 64.6 -92.666 61.917C-95.13 59.234 -97.195 56.05 -98.654 52.732C-100.112 49.413 -101.115 45.718 -101.417 42.008C-101.718 38.299 -101.495 34.32 -100.464 30.475C-99.433 26.631 -97.078 22.554 -95.229 18.942C-93.381 15.33 -91.198 11.96 -89.375 8.803C-87.553 5.646 -85.896 2.776 -84.293 0C-82.69 -2.776 -81.205 -5.349 -79.758 -7.855C-78.311 -10.362 -76.948 -12.722 -75.61 -15.04C-74.272 -17.357 -72.995 -19.57 -71.73 -21.759C-70.466 -23.949 -69.244 -26.065 -68.025 -28.177C-66.806 -30.289 -65.614 -32.353 -64.415 -34.43C-63.215 -36.508 -62.031 -38.559 -60.828 -40.644C-59.624 -42.728 -58.425 -44.805 -57.194 -46.938C-55.962 -49.07 -54.725 -51.214 -53.44 -53.44C-52.154 -55.666 -50.852 -57.921 -49.482 -60.294C-48.112 -62.667 -46.713 -65.09 -45.22 -67.677C-43.727 -70.263 -42.191 -72.924 -40.523 -75.813C-38.855 -78.702 -37.128 -81.696 -35.212 -85.01C-33.297 -88.325 -31.423 -92.392 -29.03 -95.699C-26.637 -99.006 -23.885 -102.432 -20.856 -104.852C-17.827 -107.272 -14.332 -109.029 -10.856 -110.22C-7.38 -111.412 -3.619 -112 0 -112C3.619 -112 7.38 -111.412 10.856 -110.22C14.332 -109.029 17.827 -107.272 20.856 -104.852C23.885 -102.432 26.637 -99.006 29.03 -95.699C31.423 -92.392 33.297 -88.325 35.212 -85.01C37.128 -81.696 38.855 -78.702 40.523 -75.813C42.191 -72.924 43.727 -70.263 45.22 -67.677C46.713 -65.09 48.112 -62.667 49.482 -60.294C50.852 -57.921 52.154 -55.666 53.44 -53.44C54.725 -51.214 55.962 -49.07 57.194 -46.938C58.425 -44.805 59.624 -42.728 60.828 -40.644C62.031 -38.559 63.215 -36.508 64.415 -34.43C65.614 -32.353 66.806 -30.289 68.025 -28.177C69.244 -26.065 70.466 -23.949 71.73 -21.759C72.995 -19.57 74.272 -17.357 75.61 -15.04C76.948 -12.722 78.311 -10.362 79.758 -7.855C81.205 -5.349 82.69 -2.776 84.293 0Z" fill="#8f6bff"/><path data-sq-eye="0" d="M-20 -8A20 20 0 0 1 0 -28L0 -28A20 20 0 0 1 20 -8L20 8A20 20 0 0 1 0 28L0 28A20 20 0 0 1 -20 8Z" transform="matrix(0.96 -0.09 0.14 0.97 -19.853 19.822)" fill="#fff"/><path data-sq-eye="1" d="M-20 -8A20 20 0 0 1 0 -28L0 -28A20 20 0 0 1 20 -8L20 8A20 20 0 0 1 0 28L0 28A20 20 0 0 1 -20 8Z" transform="matrix(0.88 0.09 -0.18 0.97 41.869 22.013)" fill="#fff"/></svg>';
export function ImaiStatus({ className: e = 'size-4', awaiting: t = !1 }) {
  let n = React.useId();
  return (
    <svg viewBox={'0 0 32 32'} fill={'none'} aria-hidden={!0} className={e}>
      <defs>
        <mask
          id={n}
          maskUnits={'userSpaceOnUse'}
          x={'0'}
          y={'0'}
          width={'32'}
          height={'32'}
        >
          <rect width={'32'} height={'32'} fill={'white'} />
          <circle cx={'27'} cy={'27'} r={'4.75'} fill={'black'} />
        </mask>
      </defs>
      <g mask={`url(#${n})`}>
        <rect
          className={t ? void 0 : 'bz-squish'}
          x={'3'}
          y={'3'}
          width={'26'}
          height={'26'}
          rx={'7'}
          fill={t ? 'var(--background)' : 'var(--primary)'}
          stroke={t ? 'var(--muted-foreground)' : 'none'}
          strokeWidth={'1.5'}
        />
        <g
          className={t ? void 0 : 'bz-gaze'}
          fill={t ? 'var(--foreground)' : 'var(--primary-foreground)'}
        >
          <rect x={'10.5'} y={'13'} width={'3'} height={'4'} rx={'1.5'} />
          <rect x={'18.5'} y={'13'} width={'3'} height={'4'} rx={'1.5'} />
        </g>
      </g>
      <circle cx={'27'} cy={'27'} r={'3.25'} fill={'var(--primary)'} />
    </svg>
  );
}
export const ja = 'rgb(123 92 219)';
export const Ma = {
  run: 'mt-1',
  turn: 'mt-4',
  pause: 'mt-8',
};
export function ChatMessage({ children: e, from: t }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div className={'bz-grow'}>
      <div>
        <div className={t === 'user' ? 'bz-sent' : 'bz-recv'}>
          <div>{translateShowcaseNode(showcaseCopy, e)}</div>
        </div>
      </div>
    </div>
  );
}
export function CompletionSticker() {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <span
      role={'img'}
      aria-label={translateShowcaseNode(showcaseCopy, 'Sticker: a happy IMAI')}
      className={
        'pointer-events-none absolute -top-8 -right-5 size-[60px] rotate-[9deg]'
      }
    >
      <span className={'bz-stick bz-diecut grid size-full place-items-center'}>
        <ImaiAvatar svg={ka} size={40} />
      </span>
    </span>
  );
}
export function ChatBubble({
  from: e,
  gap: t,
  children: n,
  label: r,
  sticker: i,
}) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let a = e === 'user';
  return (
    <div
      role={'group'}
      aria-label={translateShowcaseNode(showcaseCopy, r)}
      className={`flex w-full ${a ? 'justify-end' : 'justify-start'} ${Ma[t]}`}
    >
      <div className={'relative max-w-[88%]'}>
        <p
          className={`rounded-[20px] px-4 py-2.5 text-[15px] leading-5 [overflow-wrap:anywhere] ${a ? 'text-white' : 'text-ink bg-[var(--muted)]'}`}
          style={
            a
              ? {
                  backgroundColor: ja,
                }
              : void 0
          }
        >
          {translateShowcaseNode(showcaseCopy, n)}
        </p>
        {translateShowcaseNode(showcaseCopy, i ? <CompletionSticker /> : null)}
      </div>
    </div>
  );
}
export function TypingIndicator({ gap: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div
      role={'status'}
      aria-label={translateShowcaseNode(showcaseCopy, 'IMAI is typing')}
      className={`flex w-full ${Ma[e]}`}
    >
      <div
        className={
          'flex h-9 items-center gap-1 rounded-[20px] bg-[var(--muted)] px-4'
        }
      >
        {translateShowcaseNode(
          showcaseCopy,
          [0, 160, 320].map((e) => (
            <span
              key={e}
              aria-hidden={!0}
              className={
                'bz-dot size-2 rounded-full bg-[color-mix(in_oklch,var(--muted-foreground)_70%,transparent)]'
              }
              style={{
                animationDelay: `${e}ms`,
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}
export function MessageTime({ label: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <p className={'num text-ink-3 me-3 mt-0.5 text-end text-[12px] leading-4'}>
      {translateShowcaseNode(showcaseCopy, e)}
    </p>
  );
}
export function ChatTime({ label: e, gap: t }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <p
      className={`num text-ink-3 mb-0.5 text-center text-[12px] leading-4 ${t === 'top' ? 'mt-2' : 'mt-6'}`}
    >
      {translateShowcaseNode(showcaseCopy, e)}
    </p>
  );
}
